/**
 * #2133(社長指示2026-10-13): admin(運営)は自身の Custom Claims に companyId
 * を持たないため、companyAssociate と同じ画面(生徒・講師・担当者アカウント・
 * レッスン・シーケンス・ポーズ・スタジオ・マット)を使うには「今どの会社を
 * 管理しているか」を別途持つ必要がある。この「会社の管理コンテキスト」を
 * 持つ Context。
 *
 * 設計:
 * - admin だけが使う。companyAssociate/studioAssociate は自分の companyId
 *   claim をそのまま使うため、このコンテキストは常に null を返す(isAdmin
 *   でなければ無条件に無効化する。URL の companyId パラメータを他ロールが
 *   付けても無視する、という要件をここで満たす)。
 * - 選んだ会社は URL の search param(?companyId=)と sessionStorage の両方に
 *   保持する。タブを再読み込みしても、companyId クエリ付きの画面(例:
 *   /instructors?companyId=xxx)を直接開いても、管理中の会社が復元できる。
 * - 復元時は fetchCompany で実在を確認してから採用する(存在しない・読めない
 *   companyId を手元に残さない)。確認が済むまでは activeCompanyId は null
 *   のまま(fail-closed。確認前に他社のデータを一瞬見せない)。
 * - デフォルト値(Provider 無しで useActiveCompany を呼んだ場合)は
 *   「アクティブな会社なし」の no-op 実装にする。既存の多数のページ
 *   テスト(AuthContext.Provider だけを与えるもの)がこの Provider を
 *   知らなくても壊れないようにするため(useAuth のような「Provider 必須」
 *   の強い制約にはしない)。
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { fetchCompany } from '@/lib/firebase/companies';
import { logger } from '@/lib/logger';

const STORAGE_KEY = 'yoctoApp.admin.activeCompany';
const COMPANY_ID_PARAM = 'companyId';

export interface ActiveCompanyContextType {
  /** admin が今管理している会社の companyId。admin でなければ常に null。 */
  activeCompanyId: string | null;
  /** 上部バーの表示用の会社名(読めなかった場合は null)。 */
  activeCompanyName: string | null;
  /** 会社を選ぶ(CompanyList の「この会社を管理」)。admin でなければ何もしない。 */
  setActiveCompany: (companyId: string, companyName: string) => void;
  /** 管理中の会社を解除する(切り替え)。 */
  clearActiveCompany: () => void;
}

const NOOP_VALUE: ActiveCompanyContextType = {
  activeCompanyId: null,
  activeCompanyName: null,
  setActiveCompany: () => {},
  clearActiveCompany: () => {},
};

// eslint-disable-next-line react-refresh/only-export-components
export const ActiveCompanyContext = createContext<ActiveCompanyContextType>(NOOP_VALUE);

// eslint-disable-next-line react-refresh/only-export-components
export function useActiveCompany(): ActiveCompanyContextType {
  return useContext(ActiveCompanyContext);
}

interface StoredActiveCompany {
  id: string;
  name: string;
}

function readStoredActiveCompany(): StoredActiveCompany | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof (parsed as StoredActiveCompany).id === 'string' &&
      typeof (parsed as StoredActiveCompany).name === 'string'
    ) {
      return parsed as StoredActiveCompany;
    }
    return null;
  } catch (error) {
    logger.warn('Failed to read active company from sessionStorage', { reason: String(error) });
    return null;
  }
}

function writeStoredActiveCompany(value: StoredActiveCompany): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch (error) {
    logger.warn('Failed to write active company to sessionStorage', { reason: String(error) });
  }
}

function clearStoredActiveCompany(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    logger.warn('Failed to clear active company from sessionStorage', { reason: String(error) });
  }
}

interface ActiveCompanyProviderProps {
  children: ReactNode;
}

/**
 * admin 用の実装。App.tsx で認証済みレイアウトの内側(Router の内側)に配置する。
 * companyAssociate/studioAssociate がマウントしても activeCompanyId は常に
 * null になる(isAdmin でなければ何もしない)ため、他ロールへの影響は無い。
 */
export function ActiveCompanyProvider({ children }: ActiveCompanyProviderProps) {
  const { roleFlags } = useAuth();
  const isAdmin = roleFlags.isAdmin;
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState<StoredActiveCompany | null>(null);
  const restoreStartedRef = useRef(false);

  // 他ロールに切り替わった場合(通常は起こらないが、サインアウト/サインイン
  // の境界で一瞬 role が変わる可能性がある)は、下の useMemo の
  // `isAdmin ? state?.id ?? null : null` により activeCompanyId は常に
  // null になる。内部の state 自体をここで消す必要はない(消すための
  // setState をエフェクト内で無条件に呼ぶと react-hooks/set-state-in-effect
  // に抵触するため、公開値側だけで無効化する設計にする)。

  // 初回マウント時に、URL の companyId クエリ(優先)または sessionStorage
  // から復元する。fetchCompany で実在を確認できたものだけを採用する
  // (#2133: 「アクティブな会社は companies 一覧から来たものであることを
  // 検証する」要件)。
  useEffect(() => {
    if (!isAdmin || restoreStartedRef.current) {
      return;
    }
    restoreStartedRef.current = true;

    const fromUrl = searchParams.get(COMPANY_ID_PARAM);
    const stored = readStoredActiveCompany();
    const candidateId = fromUrl ?? stored?.id ?? null;
    if (!candidateId) {
      return;
    }

    let active = true;
    void fetchCompany(candidateId)
      .then((company) => {
        if (!active) return;
        if (company) {
          const value = { id: candidateId, name: company.name };
          setState(value);
          writeStoredActiveCompany(value);
        } else {
          clearStoredActiveCompany();
        }
      })
      .catch((error: unknown) => {
        logger.error('Failed to restore active company', error);
        if (active) {
          clearStoredActiveCompany();
        }
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 初回マウント時のみ実行する(再実行は不要)。
  }, [isAdmin]);

  const setActiveCompany = useCallback(
    (companyId: string, companyName: string) => {
      if (!isAdmin) return;
      const value = { id: companyId, name: companyName };
      setState(value);
      writeStoredActiveCompany(value);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set(COMPANY_ID_PARAM, companyId);
        return next;
      });
    },
    [isAdmin, setSearchParams]
  );

  const clearActiveCompany = useCallback(() => {
    setState(null);
    clearStoredActiveCompany();
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete(COMPANY_ID_PARAM);
      return next;
    });
  }, [setSearchParams]);

  const value = useMemo<ActiveCompanyContextType>(
    () => ({
      activeCompanyId: isAdmin ? state?.id ?? null : null,
      activeCompanyName: isAdmin ? state?.name ?? null : null,
      setActiveCompany,
      clearActiveCompany,
    }),
    [isAdmin, state, setActiveCompany, clearActiveCompany]
  );

  return <ActiveCompanyContext.Provider value={value}>{children}</ActiveCompanyContext.Provider>;
}

/**
 * ページが実際に使う companyId。admin は activeCompanyId(選んだ会社が無い
 * 間は空文字列)、companyAssociate/studioAssociate は Custom Claims の
 * companyId をそのまま使う。既存ページの `roleFlags.companyId ?? ''` を
 * この hook に置き換えるだけで admin 対応になる。
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useEffectiveCompanyId(): string {
  const { roleFlags } = useAuth();
  const { activeCompanyId } = useActiveCompany();
  return roleFlags.isAdmin ? activeCompanyId ?? '' : roleFlags.companyId ?? '';
}

/**
 * スタジオを選ぶ UI(MatList/LessonList/SequenceList 等)を出すべきか。
 * companyAssociate は元から選ぶ(自分の studioId claim を持たない運用がある
 * ため)。admin も会社全体を管理するため、常に選ぶ側として扱う
 * (studioAssociate のように単一の自スタジオを前提にできない)。
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useCanSelectStudio(): boolean {
  const { roleFlags } = useAuth();
  return roleFlags.isCompanyAssociate || roleFlags.isAdmin;
}
